import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge } from "@material-ui/core";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import TrackChangesIcon from "@material-ui/icons/TrackChanges";
import PhoneForwardedIcon from "@material-ui/icons/PhoneForwarded";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import VpnLockOutlinedIcon from "@material-ui/icons/VpnLockOutlined";
import StorefrontOutlinedIcon from "@material-ui/icons/StorefrontOutlined";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import CampaignIcon from "@material-ui/icons/RecordVoiceOver";
import WebhookIcon from "@material-ui/icons/SettingsInputAntenna";
import CallSplitIcon from "@material-ui/icons/CallSplit";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { getWhatsappProvider } from "../config";

function ListItemLink(props) {
  const { icon, primary, to, className } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItem button component={renderLink} className={className}>
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} />
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const { drawerClose } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING" ||
            whats.status === "WAITING_AGENT" ||
            whats.status === "DEVICE_OFFLINE"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  return (
    <div onClick={drawerClose}>
      <ListItemLink
        to="/"
        primary="Dashboard"
        icon={<DashboardOutlinedIcon />}
      />
      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
            <SyncAltIcon />
          </Badge>
        }
      />
      {(getWhatsappProvider() === "meta-cloud" ||
        (whatsApps || []).some(
          w => w.provider === "meta-cloud" || w.coexistence
        )) && (
        <>
          <ListItemLink
            to="/waba"
            primary={i18n.t("mainDrawer.listItems.waba")}
            icon={<StorefrontOutlinedIcon />}
          />
          <ListItemLink
            to="/meta"
            primary={i18n.t("mainDrawer.listItems.meta")}
            icon={<AssignmentTurnedInOutlinedIcon />}
          />
        </>
      )}
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
      />

      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
      />
      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
      />
      <ListItemLink
        to="/chat-flows"
        primary={i18n.t("mainDrawer.listItems.chatFlows")}
        icon={<CallSplitIcon />}
      />
      <ListItemLink
        to="/pixel"
        primary={i18n.t("mainDrawer.listItems.pixel")}
        icon={<TrackChangesIcon />}
      />
      <ListItemLink
        to="/campanhas"
        primary={i18n.t("mainDrawer.listItems.campanhas")}
        icon={<CampaignIcon />}
      />
      <ListItemLink
        to="/counter-numbers"
        primary={i18n.t("mainDrawer.listItems.counterNumbers")}
        icon={<PhoneForwardedIcon />}
      />
      <ListItemLink
        to="/lead-messages"
        primary={i18n.t("mainDrawer.listItems.leadMessages")}
        icon={<ForumOutlinedIcon />}
      />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader inset>
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
            />
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
            />
            <ListItemLink
              to="/proxy"
              primary={i18n.t("mainDrawer.listItems.proxy")}
              icon={<VpnLockOutlinedIcon />}
            />
            <ListItemLink
              to="/webhook"
              primary={i18n.t("mainDrawer.listItems.webhook")}
              icon={<WebhookIcon />}
            />
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
